<template>
  <div class="table-of-content">
    <p class="h6">
      Table of Content
    </p>
    <hr>
    <ul>
      <li
        v-for="(heading, i) in headings[$store.state.calculators.calculator]"
        :key="i"
        :data-section="`heading${i + 1}`"
        :class="{ active: activeSection === `heading${i + 1}` }"
        @click="scrollToSection"
      >
        <span>{{ heading }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  data() {
    return {
      headings: {
        confidenceInterval:
        [
          'Confidence Interval Calculator',
          'What Is a Confidence Interval?',
          'Which Confidence Level Should You Use?',
          'Calculating the Confidence Interval (Formula)',
          'How to Calculate a Confidence Interval with Precision',
        ],
        meanMedianMode: [
          'What Are Mean, Median, and Mode?',
          'What Is the Mean?',
          'What Is the Median?',
          'What Is the Mode?',
          'Comparison of Mean, Median, and Mode',
        ],
        skewness: [
          'What Is Skewness?',
          'How to Handle Skew Data',
          'How to Interpret the Coefficient of Skewness',
          'What is the Difference Between Skewness and Kurtosis?',
        ],
        kurtosis: [
          'What Is Kurtosis?',
          'What Is Excess Kurtosis?',
          'Types of Kurtosis',
          'How to Interpret the Kurtosis Coefficient',
          'What Is the Difference Between Skewness and Kurtosis?',
        ],
        standardDeviation: [
          'What Is Standard Deviation?',
          'What Does Standard Deviation Mean?',
          'Why Is Standard Deviation Important?',
          'How to Calculate the Standard Deviation',
          'Standard Deviation vs Variance',
        ],
        variance: [
          'What Is Variance?',
          'What Is the Importance of Variance in Statistics?',
          'How to Calculate Variance',
          'What Are the Advantages and Disadvantages of Variance?',
          'What Are the Differences Between Variance and Standard Deviation?',
        ],
        coefficientOfVariation: [
          'What Is the Coefficient of Variation (CV)?',
          'What Does Coefficient of Variation Mean?',
          'How to Calculate the Coefficient of Variation',
          'How to Interpret the Coefficient of Variation',
          'Applications of Coefficient Variation',
          'What Are the Advantages and Disadvantages of the Coefficient of Variation?',
          'Coefficient of Variation vs Standard Deviation',
        ],
        linearRegression: [
          'Linear Regression Calculator',
          'What Is Regression Analysis?',
          'How to Find the Linear Regression Equation',
          'Interpretation of Linear Regression Results',
        ],
        pValue: [
          'What Is P-Value in Statistics?',
          'How to Calculate the P-Value',
          'How to Find the P-Value Using the P-Value Calculator?',
          'How to Interpret the P-Value',
          'Does the P-Value Depend on the Alternative Hypothesis?',
          'How to Obtain the P-Value from a Z-Score',
          'How to Obtain the P-Value from a T-Score',
          'How to Obtain the P-Value from a Chi-Score',
          'How to Find the P-Value Using the F-Statistic?',
        ],
        hypothesisTest: [
          'What Is Hypothesis Testing?',
          'The Process of Hypothesis Testing',
          'Null Hypothesis vs Alternative Hypothesis',
          'One-Tailed vs Two-Tailed Hypothesis Tests',
          'What Is a Test Statistic?',
          'Significance Level in Hypothesis Testing',
          'Rejection Region in Hypothesis Testing',
          'Type I and Type II Error in Hypothesis Testing',
          'Hypothesis Testing with P-Value',
        ],
        differenceInMeans: [
          'Hypothesis Testing the Difference in Means',
          'What are Hypothesis Tests for Differences of Means?',
          'Two-Sided vs. One-Sided Hypotheses for Difference in Means',
          'T-Statistic for Difference in Means',
          'Level of Significance and Rejection Region for Comparison of Means',
          'P-Value for the Difference between Two Means',
          'How to Choose Test Statistic for the Difference between Two Means',
        ],
      },
      activeSection: '',
    };
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('scroll', this.handleScroll);
    });
  },
  methods: {
    async scrollToSection(e) {
      let yOffset;
      let buffer = 90;
      if (this.$store.state.layout === 'desktop') {
        if (document.querySelector('.banner-holder')) {
          buffer += document.querySelector('.banner-holder').clientHeight;
        }
        yOffset = -buffer;
      } else {
        buffer = this.$config.project === 'lms' ? 68 : 72;
        if (document.querySelector('.banner-holder')) {
          buffer += document.querySelector('.banner-holder').clientHeight;
        }
        yOffset = -buffer;
      }

      const section = `${e?.target?.getAttribute('data-section') || e?.target?.closest('li')?.getAttribute('data-section')}`;
      const element = document.querySelector(`#${section}`);
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      setTimeout(() => {
        this.clickedSection = section;
        window.scrollTo({ top: y, behavior: 'smooth' });

        setTimeout(() => {
          this.clickedSection = null;
        }, 500);
      }, 200);
    },
    handleScroll() {
      let buffer = 92;
      if (document.querySelector('.banner-holder')) {
        buffer += document.querySelector('.banner-holder').clientHeight;
      }
      const sections = document.querySelectorAll('.article-section');
      const current = Array.from(sections).filter((s) => s.getBoundingClientRect().top - buffer <= 0);
      const element = current[current.length - 1];

      if (element) {
        this.activeSection = element.id;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.table-of-content {
  height: fit-content;
  position: sticky;
  top: 110px;

  .h6 {
    margin: 0 0 var(--spacing-36)  var(--spacing-16);
  }

  ul {
    margin: 0;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      list-style: none;
      font-size: 14px;

      span {
        padding: var(--spacing-12) 0 var(--spacing-12) var(--spacing-16);
      }

      &.active,
      &:hover {
        background: var(--main-light-shade-2);
        border-radius: 5px;
        font-weight: 600;
        cursor: pointer;
      }
    }
  }

  hr {
    margin: 0;
    border: 1px solid var(--main-light-shade-3);
    margin-bottom: var(--spacing-24);
  }
}

@include bp($bp-mobile) {
  .table-of-content {
    height: unset;
    margin-bottom: var(--spacing-60);

    ::v-deep .ab-banner.side {
      display: block;
      margin-bottom: 100px;

      .banner-title {
        .first {
          font-size: 18px;
          line-height: 20px;
          font-weight: 500;

          .dash {
            width: 46px;
          }
        }

        .second {
          font-size: 20px;
          line-height: 20px;
          font-weight: 600;
        }

        .title {
          font-size: 38px;
          line-height: 32px;
          font-weight: 700;
        }

        .third {
          font-size: 28px;
          line-height: 22px;
          font-weight: 600;
        }
      }
    }

    .h6 {
      margin: var(--spacing-12) 0 var(--spacing-28) var(--spacing-16);
    }

    hr {
      margin-bottom: var(--spacing-16);
    }
  }
}
</style>
